/**
 * Live wins feed.
 *
 * Ported from the sidebar panel on main, with one change: it reads REAL settled
 * wins from the contract instead of inventing them.
 *
 * The original filled the panel with fabricated rows, each carrying a
 * "🛡 verify 0x1a2…b3c" chip and a footer reading "every win verifiable
 * on-chain". Those hashes pointed at nothing. On a testnet mockup that is a
 * placeholder; on this branch the game settles real ETH on mainnet, so shipping
 * invented wins labelled verifiable would be a plain misrepresentation — and
 * README-LAUNCH.md Gate 3 already flags simulated activity feeds as a
 * misleading-practice risk.
 *
 * Since ScratchyGame emits `Played` with the stake, the payout and the
 * transaction that settled it, the honest version is also the easy one: the
 * verify chips are real links to real transactions, and the amounts are what
 * the contract actually paid.
 *
 * If no contract is configured the panel says it is idle rather than
 * fabricating activity to fill the space.
 */
import { Contract, type JsonRpcProvider, type Log } from "ethers";
import { archiveProvider } from "./game";
import { CHAIN_CONFIG, SETTLEMENT, isOnchainConfigured, formatWei } from "./config";

const PLAYED_ABI = [
  "event Played(address indexed player, bytes32 indexed nonce, address currency, uint8 tierId, uint8 count, uint256 stake, uint256 payout, bytes32 externalRandomness, bytes32 randomness)"
];

/**
 * Block the CURRENT ScratchyGame was deployed at.
 *
 * Moved with the multi-currency redeploy. Pointing it at the old contract's
 * block would still return logs — from an address that is no longer the game —
 * so this has to move whenever gameAddress does.
 *
 * A fixed lookback has to be generous here: Robinhood Chain produces ~10 blocks
 * a second — about 840,000 a day — so an hour's worth of blocks is nothing and
 * a short window would leave the feed permanently empty.
 *
 * It also has to be BOUNDED, which this did not used to be. Querying from the
 * deploy block was exact and, when written, cheap: the RPC capped nothing. It
 * caps eth_getLogs at 10,000,000 blocks now, and at this block rate the chain
 * passed that 12 days after launch — so the query began failing outright and
 * the feed showed "unavailable" rather than any wins. A span measured from the
 * head instead of from deployment cannot age into that wall.
 *
 * The cost is that wins older than the window are not shown. For a feed
 * labelled "Live wins" that is the right trade; the alternative is a feed that
 * shows nothing at all.
 */
const DEPLOY_BLOCK = 61_432_232;
/** Comfortably under the RPC's 10,000,000-block ceiling. ~10 days of history. */
const MAX_LOG_SPAN = 9_000_000;
const POLL_MS = 15_000;
const MAX_ROWS = 5;

export interface Win {
  player: string;
  stake: bigint;
  payout: bigint;
  txHash: string;
  blockNumber: number;
}

const shortAddr = (a: string) => a.slice(0, 6) + "…" + a.slice(-3);
const shortTx = (h: string) => h.slice(0, 5) + "…" + h.slice(-3);

/* Deterministic identicon, ported unchanged from the original panel. */
function identicon(seed: string): string {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  let s = h >>> 0 || 1;
  const r = () => {
    s ^= s << 13; s >>>= 0;
    s ^= s >>> 17;
    s ^= s << 5; s >>>= 0;
    return s / 4294967296;
  };
  const hue = Math.floor(r() * 360);
  const c = `hsl(${hue} 62% 56%)`;
  const c2 = `hsl(${(hue + 40) % 360} 55% 46%)`;
  const u = 6;
  let cells = "";
  for (let y = 0; y < 5; y++) {
    for (let x = 0; x < 3; x++) {
      if (r() > 0.5) {
        cells += `<rect x="${x * u}" y="${y * u}" width="${u}" height="${u}" fill="${r() > 0.5 ? c : c2}"/>`;
        if (x < 2) cells += `<rect x="${(4 - x) * u}" y="${y * u}" width="${u}" height="${u}" fill="${c}"/>`;
      }
    }
  }
  return `<svg width="28" height="28" viewBox="0 0 30 30" style="background:#15170f">${cells}</svg>`;
}

function rowHtml(w: Win): string {
  const payoutStr = formatWei(w.payout);
  const stakeStr = formatWei(w.stake);
  const mult = w.stake > 0n ? Number((w.payout * 100n) / w.stake) / 100 : 0;
  const big = w.stake > 0n && w.payout >= w.stake * 25n;
  const url = `${CHAIN_CONFIG.explorer}/tx/${w.txHash}`;
  return `<div class="wf-row${big ? " big" : ""}">
    <div class="wf-av">${identicon(w.player)}</div>
    <div class="wf-who">
      <div class="wf-addr">${shortAddr(w.player)}</div>
      <div class="wf-sub">
        <a class="wf-vchip" href="${url}" target="_blank" rel="noopener noreferrer"
           title="Open the settling transaction">🛡 verify <span class="tx">${shortTx(w.txHash)}</span></a>
      </div>
    </div>
    <div class="wf-right">
      <div class="wf-amt">+${payoutStr}</div>
      <div class="wf-mult">${stakeStr} → ${mult.toLocaleString()}×</div>
    </div>
  </div>`;
}

async function fetchWins(provider: JsonRpcProvider): Promise<Win[]> {
  const game = new Contract(CHAIN_CONFIG.gameAddress, PLAYED_ABI, provider);
  const head = await provider.getBlockNumber();
  /* Never earlier than deployment (nothing to find) and never more than the
     RPC will serve (the whole query fails, not just the excess). */
  const from = Math.max(DEPLOY_BLOCK, head - MAX_LOG_SPAN);
  const logs = (await game.queryFilter(game.filters.Played(), from, head)) as unknown as (Log & {
    args: any;
  })[];

  return logs
    /* Only the asset the game currently settles in. The contract is
       currency-agnostic and this deployment has already run on MockUSD (6dp)
       before moving to native ETH (18dp); mixing them would render those old
       plays as nonsense fractions of a gwei. */
    .filter(l => String(l.args.currency).toLowerCase() === SETTLEMENT.address.toLowerCase())
    .map(l => ({
      player: l.args.player as string,
      stake: l.args.stake as bigint,
      payout: l.args.payout as bigint,
      txHash: l.transactionHash,
      blockNumber: l.blockNumber
    }))
    /* Only wins. A losing card is a settled play too, but this panel is
       explicitly "live wins" — showing zeros would misrepresent it the other
       way. Sub-stake returns are excluded for the same reason: they are not
       wins, and the honest-outcome rule says so. */
    .filter(w => w.payout > w.stake)
    .sort((a, b) => b.blockNumber - a.blockNumber)
    .slice(0, MAX_ROWS);
}

export function startWinsFeed(): void {
  const panel = document.getElementById("winfeed");
  const rows = document.getElementById("wfRows");
  const badge = document.getElementById("wfBadge");
  const msg = document.getElementById("wfMsg");
  if (!panel || !rows || !badge || !msg) return;

  if (!isOnchainConfigured()) {
    /* No contract: say the panel is idle. The original filled this state with
       invented rows; an empty panel is more honest than a fake busy one. */
    badge.textContent = "Idle";
    rows.innerHTML = `<div class="wf-empty">No contract configured — no settled wins to show.</div>`;
    msg.innerHTML = `🛡 <b>Demo build</b> · outcomes drawn locally · nothing settled on-chain`;
    return;
  }

  badge.textContent = "Live";
  msg.innerHTML = `🛡 <b>${CHAIN_CONFIG.chainName}</b> · real settled wins · tap verify for the transaction`;

  /* The archive endpoint, not the live one. This feed reads every Played event
     since the deploy block, and the live endpoint refuses a range that wide. */
  const provider = archiveProvider();
  let lastTop = "";

  const paint = async () => {
    try {
      const wins = await fetchWins(provider);
      if (!wins.length) {
        rows.innerHTML = `<div class="wf-empty">No wins settled yet. Yours could be the first.</div>`;
        lastTop = "";
        return;
      }
      /* Only rewrite when the newest win changes, so the slide-in animation
         does not replay on every poll. */
      const top = wins[0].txHash;
      if (top === lastTop) return;
      lastTop = top;
      rows.innerHTML = wins.map(rowHtml).join("");
    } catch (e) {
      /* A log-query failure must never take the game down with it — but it must
         not be silent either. Swallowing this turned "the feed is empty" into a
         mystery that needed a container rebuild to diagnose. */
      console.warn("[wins] log query failed:", e);
      if (!rows.querySelector(".wf-row")) {
        rows.innerHTML = `<div class="wf-empty">Wins feed unavailable right now.</div>`;
      }
    }
  };

  /* A transient RPC blip on the very first query would otherwise leave
     "unavailable" on screen for a full poll interval, which reads as broken
     rather than as a hiccup. Retry the first paint quickly a couple of times,
     then settle into the normal cadence. */
  (async () => {
    for (const delay of [0, 2000, 5000]) {
      if (delay) await new Promise(r => setTimeout(r, delay));
      await paint();
      if (rows.querySelector(".wf-row")) break;
    }
    setInterval(paint, POLL_MS);
  })();
}
